import React, { useContext } from 'react';
import { TeApiContext } from '@ecommerce/nest-ssr/common';
import { ProductInterface } from 'interfaces/product.interface';
import Link from 'atomic/utilization/link';
import Stars from 'atomic/utilization/stars';
import Price from 'atomic/utilization/price';
import Button from 'atomic/utilization/button';
import flectionNumeral from 'helpers/number';
import ProductListImpression from 'components/impressions/product-impression/list/product-list-impression';

import './product-box.scss';

interface ProductBoxProps {
    product: ProductInterface;
    compareButton?: boolean;
    showDiscount?: boolean;
    loading?: 'lazy' | 'eager';
}

const ProductBox: React.FC<ProductBoxProps> = ({ product, compareButton, showDiscount, loading = 'lazy' }) => {
    const api = useContext(TeApiContext);

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const isMobile = api.getDevice() === 'mobile';

    return (
        <ProductListImpression trackedData={product} className="product-box-container">
            { /* eslint-disable-next-line react/jsx-no-target-blank */ }
            <a
                className="product-box"
                href={product.offers.count === 1 ? product.offers.items[0].url : product.url}
                rel={product.offers.count === 1 ? 'noopener noreferrer' : void 0}
                target={product.offers.count === 1 ? '_blank' : void 0}
            >
                {product.offers.count === 1 && (
                    <object className="product-box__link" aria-label="Strona z ofertami">
                        <a href={product.url} aria-label="Strona z ofertami">
                            <i className="product-box__icon icon-info"/>
                        </a>
                    </object>
                )}
                <div className="product-box__image">
                    <img src={product.photos.main?.thumb} alt={product.name} width={180}
                        loading={loading}
                    />
                    {showDiscount && (
                        <div className="product-box__discount">
                            -
                            {((product.maxPrice - product.minPrice) / product.maxPrice * 100).toFixed(0)}
                            %
                        </div>
                    )}
                </div>
                <div className="product-box__title">
                    {product.name}
                </div>
                <div className="product-box__border"/>
                {product.opinions.average > 0 && (
                    <div className="product-box__stars">
                        <Stars className="sm star-rating--filled-orange" width={product.opinions.average}/>
                    </div>
                )}
                {showDiscount && (
                    <div className="product-box__old-price">
                        <Price price={product.maxPrice} size={'md'} weight={'700'}
                            crossed={true} color={'gray'}
                        />
                    </div>
                )}
                <div className={`product-box__price ${showDiscount ? 'product-box__price--discount' : ''}`}>
                    <span>
                        od
                        {' '}
                    </span>
                    <Price price={product.price} size={isMobile ? 'md' : 'lg'} weight={'700'}
                        color={showDiscount ? 'red' : 'dark'}
                    />
                </div>
                <object
                    className="product-box__button"
                    aria-label={product.offers.count > 1 && !compareButton ? 'Porównaj' : 'dz do sklepu'}
                >
                    <Link
                        href={product.offers.count === 1 || compareButton ? product.offers.items[0].url : product.url}
                        rel={compareButton ? 'noopener noreferrer' : void 0}
                        target={compareButton ? '_blank' : void 0}
                        size={'md'}
                        type={'button'}
                        uppercase={true}
                        color={'orange'}
                        block={true}
                    >
                        {product.offers.count > 1 && !compareButton
                            ? (
                                <>
                                Porównaj
                                    {' '}
                                    <span className="product-box__offer-title">
                                    oferty
                                    </span>
                                </>
                            )
                            : (
                                <span className="product-box__one-offer-title">
                                Idź do sklepu
                                </span>
                            )}
                    </Link>
                </object>
                {compareButton
                    ? (
                        <Button size={'md'} uppercase={true} color={'blue'}
                            tag={'span'} block={true}
                        >
                            <span className="product-box__compare">
                                {`Porównaj ${flectionNumeral(product.offers.count, ['oferta', 'oferty', 'ofert'])}`}
                            </span>
                            <span className="product-box__compare-mobile">
                            Porównaj ceny
                            </span>
                        </Button>
                    )
                    : (
                        <div className="product-box__offers">
                            {product.offers.count === 1
                                ? product.offers.items[0].dealer.name
                                : flectionNumeral(product.offers.count, ['oferta', 'oferty', 'ofert'])}
                        </div>
                    )}

            </a>
        </ProductListImpression>
    );
};

export default ProductBox;
